/**
 * Service for parsing LINESTRING geometry data
 */

import { logService, LOG_CATEGORIES } from '@/services/logService'

/**
 * Parse LINESTRING geometry into coordinate pairs
 * @param lineString - LINESTRING format: "LINESTRING (lng1 lat1, lng2 lat2, ...)" or "LINESTRING(lng1 lat1, lng2 lat2, ...)"
 * @returns Array of [lng, lat] coordinate pairs
 */
export const parseLineString = (lineString: string): [number, number][] => {
  try {
    // Parse the LINESTRING format: "LINESTRING (lng1 lat1, lng2 lat2, ...)" or "LINESTRING(lng1 lat1, lng2 lat2, ...)"
    const coordinateString = lineString.replace(/^LINESTRING\s*\(|\)$/g, '')
    const coordinates = coordinateString.split(',').map((coord) => {
      const parts = coord.trim().split(/\s+/) // Split by any whitespace
      if (parts.length !== 2) return [NaN, NaN] as [number, number]
      const [lng, lat] = parts.map(Number)
      return [lng, lat] as [number, number]
    })

    // Filter out invalid coordinates
    return coordinates.filter(([lng, lat]) => !isNaN(lng) && !isNaN(lat))
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to parse LINESTRING', { lineString, error })
    return []
  }
}

/**
 * Build a LINESTRING string from segments (inverse of parseLineString).
 * Returns undefined when there are fewer than 2 points.
 */
export const buildLineStringFromSegments = (
  segments: { points: { lat: number; lon: number }[] }[],
): string | undefined => {
  const coords: string[] = []
  for (const segment of segments) {
    for (const point of segment.points) {
      coords.push(`${point.lon} ${point.lat}`)
    }
  }
  return coords.length >= 2 ? `LINESTRING(${coords.join(', ')})` : undefined
}

/**
 * Calculate bounding box from coordinate arrays
 * @param coordinateArrays - Array of coordinate arrays
 * @returns Bounding box with sw and ne corners, or null if no valid coordinates
 */
export const calculateBounds = (coordinateArrays: [number, number][][]) => {
  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity

  coordinateArrays.forEach((coordinates) => {
    coordinates.forEach(([lng, lat]) => {
      if (!isNaN(lng) && !isNaN(lat)) {
        minLng = Math.min(minLng, lng)
        maxLng = Math.max(maxLng, lng)
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
      }
    })
  })

  if (minLng === Infinity) return null

  return {
    sw: [minLng, minLat] as [number, number],
    ne: [maxLng, maxLat] as [number, number],
  }
}
